// Web-push opt-in for the signed-in user's current device. Owns the browser
// permission + PushManager subscription + its Firestore doc, and exposes the state
// the settings UI renders. Modeled on useScreenWakeLock (a capability gated on a
// browser API), but enable/disable are async because they prompt for permission,
// subscribe, and write to Firestore.

import { useCallback, useEffect, useState } from 'react'
import { persistSubscription, removeSubscription } from '@/services/push/store'
import { getCurrentSubscription, requestPermissionAndSubscribe, unsubscribe } from '@/services/push/subscribe'
import { getPushSupport } from '@/services/push/support'
import { useUserData } from '@/store/userData/useUserData'

export interface PushNotificationsState {
  // Push API + service workers + Notification API are available.
  supported: boolean
  // Live browser permission ('default' | 'granted' | 'denied').
  permission: NotificationPermission
  // This device has an active subscription persisted for the user.
  subscribed: boolean
  // Running as an installed/standalone PWA (drives the iOS install hint).
  standalone: boolean
  // iOS Safari — combined with !standalone, push is unavailable until installed.
  isIOS: boolean
  // An enable/disable call is in flight (disable the toggle while true).
  busy: boolean
  // Prompt for permission, subscribe, and persist. Resolves true when the device
  // is now subscribed, false when the user denied permission. Throws on an
  // unexpected failure (subscribe/persist error) so the caller can surface it.
  enable: () => Promise<boolean>
  // Unsubscribe this device and delete its Firestore doc.
  disable: () => Promise<void>
}

export function usePushNotifications(): PushNotificationsState {
  const { uid } = useUserData()
  const [support, setSupport] = useState(() => getPushSupport())
  const [subscribed, setSubscribed] = useState(false)
  const [busy, setBusy] = useState(false)

  // Reflect an existing subscription on mount (e.g. the user already opted in on
  // this device in a previous session).
  useEffect(() => {
    if (!support.supported || !uid) {
      setSubscribed(false)
      return
    }
    let cancelled = false
    getCurrentSubscription()
      .then((sub) => {
        if (!cancelled) setSubscribed(sub !== null)
      })
      .catch(() => {
        if (!cancelled) setSubscribed(false)
      })
    return () => {
      cancelled = true
    }
  }, [support.supported, uid])

  const refreshPermission = useCallback(() => {
    setSupport(getPushSupport())
  }, [])

  const enable = useCallback(async (): Promise<boolean> => {
    if (!uid || !support.supported || busy) return false
    setBusy(true)
    try {
      const subscription = await requestPermissionAndSubscribe()
      // Permission denied — reflect the new 'denied' permission, stay unsubscribed.
      if (!subscription) {
        setSubscribed(false)
        return false
      }
      await persistSubscription(uid, subscription)
      setSubscribed(true)
      return true
    } finally {
      refreshPermission()
      setBusy(false)
    }
  }, [uid, support.supported, busy, refreshPermission])

  const disable = useCallback(async () => {
    if (!uid || busy) return
    setBusy(true)
    try {
      const subscription = await unsubscribe()
      if (subscription) await removeSubscription(uid, subscription)
      setSubscribed(false)
    } finally {
      refreshPermission()
      setBusy(false)
    }
  }, [uid, busy, refreshPermission])

  return {
    supported: support.supported,
    permission: support.permission,
    subscribed,
    standalone: support.standalone,
    isIOS: support.isIOS,
    busy,
    enable,
    disable
  }
}
